import {
  FocusEvent,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useRef,
} from 'react';
import composeRefs from '@seznam/compose-react-refs';

import { isFileValid } from '@components/patterns/Dropzone/utils';
import { cn } from '@components/primitives/utilities';

import { useDropzone } from './hook';

import css from './styles.module.css';

export interface DropzoneProps extends PropsWithChildren {
  id?: string;
  className?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Disable click to open file picker (default: false) */
  noClick?: boolean;
  /** Handler when files are dropped */
  onDrop(files: [File, ...File[]]): void;
  /** Handler when files are invalid */
  onInvalid?(): void;
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
}

export const Dropzone = forwardRef<HTMLInputElement, DropzoneProps>(
  function Dropzone(
    {
      id,
      children,
      className,
      accept,
      multiple,
      disabled,
      onDrop,
      onInvalid,
      onBlur,
      noClick = false,
    },
    ref
  ) {
    const inputRef = useRef<HTMLInputElement>(null);

    const onFileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const allFiles = Array.from(e.target.files || []);
        const validFiles = allFiles.filter((file) => isFileValid(file, accept));
        const files = multiple ? validFiles : validFiles.slice(0, 1);
        if (files.length > 0) {
          onDrop(files as [File, ...File[]]);
        } else {
          onInvalid?.();
        }
      },
      [onDrop, onInvalid, multiple, accept]
    );

    const onFileDrop = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        const allFiles = Array.from(e.dataTransfer.files);
        const validFiles = allFiles.filter((file) => isFileValid(file, accept));
        const files = multiple ? validFiles : validFiles.slice(0, 1);
        if (files.length > 0) {
          onDrop(files as [File, ...File[]]);
        } else {
          onInvalid?.();
        }
      },
      [onDrop, onInvalid, multiple, accept]
    );

    const dropzone = useDropzone<HTMLDivElement>({
      disabled,
      onDrop: onFileDrop,
    });

    return (
      <div
        className={cn(css.dropzone, className)}
        onDragOver={dropzone.onDragStart}
        onDragLeave={dropzone.onDragEnd}
        onDrop={dropzone.onDrop}
        data-dragging={dropzone.dragging}
        data-clickable={!noClick}
      >
        <input
          id={id}
          ref={composeRefs(ref, inputRef)}
          className={css.input}
          type="file"
          value=""
          title=""
          accept={accept}
          onChange={onFileChange}
          multiple={multiple}
          disabled={disabled}
          onBlur={onBlur}
        />

        {children}
      </div>
    );
  }
);
